import moment from 'moment';
import dayjs from 'dayjs';
import 'moment/locale/es';
import 'dayjs/locale/es';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { injectIntl, useIntl } from 'react-intl';
import AccionaBigButton from '../../../../components/AccionaBigButton';
import ProviderRecognised from '../../../../components/ProviderRecognised';
import '../../../../services/api/files';
import { getDocumentsByProjectId } from '../../../../services/api/project';
import UploadDocumentModal from './UploadDocumentModal';
import UploadResumeModal from './UploadResumeModal';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Providers } from '../../../../constants/providers';
import {
  createFileOnBackend,
  preprocessFile,
} from '../../../../services/api/files';
import { MainContext } from '../../../../context/MainContext';
import { PDFDocument } from 'pdf-lib';
import { useSelector } from 'react-redux';

dayjs.extend(utc);
dayjs.extend(minMax);
moment.locale(navigator.language.substring(0, 2));
dayjs.locale(navigator.language.substring(0, 2));

const Main = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { project } = useOutletContext();
  const [providerRecognised, setProviderRecognised] = useState();
  const [showModal, setShowModal] = useState(false);
  const [uploadModalData, setUploadModalData] = useState({
    total: 0,
    files: [],
    uploading: false,
    model: '',
  });
  const [preDeliveryNotes, setPreDeliveryNotes] = useState(0);
  const [preprocessedDocs, setPreprocessedDocs] = useState(0);
  const [scannedDocuments, setScannedDocuments] = useState(0);
  const id = project.id_project;
  const { me } = useSelector(({ auth }) => auth);

  const hasCemexAsProvider = useMemo(
    () =>
      project?.concrete_providers.some((cp) => cp.provider === Providers.CEMEX),
    [project?.concrete_providers],
  );

  const { setLoading, setError, setShowUploadDelayInfo } =
    useContext(MainContext);
  const pid = parseInt(id, 10);

  const getMessage = (response) => {
    let message;

    if (!response) {
      return 'Unknown document';
    }
    switch (response) {
      case 'not found template':
        message = 'notFound';
        break;
      default:
        message = response;
    }

    return message;
  };

  const extractFirstPageFromPdf = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const originalPdf = await PDFDocument.load(arrayBuffer);

    const newPdf = await PDFDocument.create();
    const [firstPage] = await newPdf.copyPages(originalPdf, [0]);
    newPdf.addPage(firstPage);

    const newPdfBytes = await newPdf.save();
    return new Blob([newPdfBytes], { type: 'application/pdf' });
  };

  const processSingleFile = async (file, model) => {
    if (file.name.endsWith('.pdf')) {
      const arrayBuffer = await file.arrayBuffer();
      const originalPdf = await PDFDocument.load(arrayBuffer);
      const pageCount = originalPdf.getPageCount();
      setShowUploadDelayInfo(pageCount >= 3);

      if (model === 'laboratory' && pageCount > 1) {
        const newFile = new File(
          [await extractFirstPageFromPdf(file)],
          file.name,
          {
            type: 'application/pdf',
          },
        );
        return newFile;
      }
    }

    return file;
  };

  const handleSingleFileUpload = async (file, allowedFileExtensions, model) => {
    if (
      allowedFileExtensions.includes(file.name.split('.').pop().toLowerCase())
    ) {
      setLoading(true);
      const processedFile = await processSingleFile(file, model);

      createFileOnBackend(processedFile, model, pid, false)
        .then(({ data }) => {
          setLoading(false);
          setShowUploadDelayInfo(false);
          if (!data.suppliers) {
            setLoading(false);
            setProviderRecognised({
              template: data.template,
              fileName: data.fileName,
              model,
            });
          } else {
            const tempFiles = [];

            for (const i in data.suppliers) {
              tempFiles.push({
                name: intl.formatMessage(
                  { id: 'page' },
                  { number: Number(i) + 1 },
                ),
                status: 'sucess',
                template: intl.formatMessage({
                  id: getMessage(data.suppliers[i]),
                }),
              });
            }
            setUploadModalData({
              files: tempFiles,
              total: data.suppliers.length,
              uploading: false,
              model: `title.${model}`,
            });
          }
        })
        .catch((error) => {
          setLoading(false);
          setError({
            status: error.response ? error.response.status : 502,
            errorType: 'UPLOAD_FILE_ERROR',
          });
        });
    } else {
      setError({
        status: 415,
        errorType: 'UPLOAD_FILE_ERROR',
      });
    }
  };

  const handleMultipleFilesUpload = async (files, model, ref) => {
    const tempFiles = [];
    setShowModal(false);
    for (const file of files) {
      tempFiles.push({
        name: file.name,
        status: 'waiting',
        template: intl.formatMessage({ id: 'waiting' }),
      });
    }
    setUploadModalData({
      files: tempFiles,
      total: files.length,
      uploading: true,
      model: `title.${model}`,
    });
    for (const [index, file] of Object.entries(files)) {
      try {
        setUploadModalData((prev) => {
          prev.files[index] = {
            name: file.name,
            status: 'uploading',
            template: intl.formatMessage({ id: 'searching' }),
          };
          return { ...prev };
        });
        const response = await preprocessFile(file, model, pid);
        if (response.data) {
          let msg;
          if (response.data.suppliers) {
            msg = (
              <>
                {response.data.suppliers.map((msg, index) => (
                  <div key={index}>
                    {intl.formatMessage({ id: 'page' }, { number: index + 1 })}:{' '}
                    {intl.formatMessage({ id: getMessage(msg) })}
                  </div>
                ))}
              </>
            );
            console.info(msg);
          } else {
            msg = intl.formatMessage({ id: getMessage(response.data) });
          }
          setUploadModalData((prev) => {
            prev.files[index] = {
              name: file.name,
              status: 'sucess',
              template: msg,
            };
            return { ...prev };
          });
        }
      } catch (e) {
        setUploadModalData((prev) => {
          prev.files[index] = {
            name: file.name,
            status: 'error',
            template: 'Error',
          };
          return { ...prev };
        });
      }
    }
    setUploadModalData((prev) => {
      return { ...prev, uploading: false };
    });
    if (document.getElementById(ref)) {
      document.getElementById(ref).value = [];
    }
  };

  useEffect(() => {
    if (uploadModalData.uploading === false)
      getDocumentsByProjectId(id).then((response) => {
        setPreDeliveryNotes(response.preDeliveryNotes);
        setPreprocessedDocs(response.preprocessedDocs);
        setScannedDocuments(
          response.scannedDocuments + response.scannedDocumentsPC,
        );
      });
  }, [id, uploadModalData.uploading]);

  const onOk = () => {
    setUploadModalData({
      total: 0,
      files: [],
      uploading: false,
      model: '',
    });
  };

  if (providerRecognised) {
    return (
      <ProviderRecognised
        fileName={providerRecognised.fileName}
        template={providerRecognised.template}
        onBack={() => {
          setProviderRecognised(null);
        }}
        model={providerRecognised.model}
      />
    );
  }

  return (
    <>
      <div className="row">
        {hasCemexAsProvider && (
          <div className="col-xs-12">
            <AccionaBigButton
              icon="cemex-tracking"
              id="cemexTracking"
              msg={intl.formatMessage({ id: `cemexTracking` })}
              display="inline"
              size="slim"
              onClick={() =>
                navigate(
                  `/app/project/${project.id_project}/quality/ocr/cemex-tracking`,
                )
              }
            />
          </div>
        )}
        <div className="col-xs-12 col-sm-6 col-md-6">
          <AccionaBigButton
            icon="read-ocr"
            id="dobleUploadButton"
            msg={intl.formatMessage({ id: `document` })}
            onClick={() => setShowModal(true)}
            disable={!me?.is_active}
          />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6">
          <AccionaBigButton
            icon="folder"
            id="templates"
            onClick={() =>
              navigate(
                `/app/project/${project.id_project}/quality/ocr/templates`,
              )
            }
            msg={intl.formatMessage({ id: `button.templates` })}
          />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6">
          <AccionaBigButton
            pendingDocs={preDeliveryNotes}
            icon="smartphone-docs"
            id="preRegisteredDocuments"
            onClick={() =>
              navigate(
                `/app/project/${project.id_project}/quality/ocr/preRegisteredDocuments`,
              )
            }
            msg={intl.formatMessage({ id: `button.preregistered.documents` })}
          />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6">
          <AccionaBigButton
            icon="prm"
            id="prmDocuments"
            onClick={() =>
              navigate(`/app/project/${project.id_project}/quality/ocr/prm`)
            }
            msg={intl.formatMessage({ id: `button.prm` })}
          />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6">
          <AccionaBigButton
            pendingDocs={preprocessedDocs}
            icon="docs-no-ok"
            id="preProcessedDocuments"
            onClick={() =>
              navigate(
                `/app/project/${project.id_project}/quality/ocr/preProcessedDocuments`,
              )
            }
            msg={intl.formatMessage({ id: `button.preprocessed.documents` })}
          />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6">
          <AccionaBigButton
            icon="docs-ok"
            id="scannedDocuments"
            pendingDocs={scannedDocuments}
            onClick={() =>
              navigate(
                `/app/project/${project.id_project}/quality/ocr/scannedDocuments`,
              )
            }
            msg={intl.formatMessage({ id: `button.scanned.documents` })}
          />
        </div>
      </div>
      <UploadDocumentModal
        open={showModal}
        onCancel={() => setShowModal(false)}
        onSingleFileUpload={handleSingleFileUpload}
        onMultipleFilesUpload={handleMultipleFilesUpload}
      />
      <UploadResumeModal
        uploadFiles={uploadModalData}
        onOk={onOk}
        onCancel={onOk}
      />
    </>
  );
};

export default injectIntl(Main);
